import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Users, Check, X, Clock, Gamepad2 } from 'lucide-react';

interface RematchVote {
  id: string;
  player_id: string;
  player_name: string;
  vote: boolean;
}

interface RematchRequestModalProps {
  initiatorName: string;
  votes: RematchVote[];
  totalPlayers: number;
  hasVoted: boolean;
  timeRemaining: number;
  onVoteYes: () => void;
  onVoteNo: () => void;
  onDismiss?: () => void;
  status: string;
}

export function RematchRequestModal({
  initiatorName,
  votes,
  totalPlayers,
  hasVoted,
  timeRemaining,
  onVoteYes,
  onVoteNo,
  onDismiss,
  status,
}: RematchRequestModalProps) {
  // Deduplicate votes by player_id to prevent showing duplicates
  const uniqueVotes = votes.reduce((acc, vote) => {
    if (!acc.find(v => v.player_id === vote.player_id)) {
      acc.push(vote);
    }
    return acc;
  }, [] as RematchVote[]);

  const yesVotes = uniqueVotes.filter(v => v.vote).length;
  const noVotes = uniqueVotes.filter(v => !v.vote).length;

  if (status !== 'pending') {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', damping: 20 }}
        >
          <Card className="w-full max-w-md p-6 bg-card border-2 border-primary/30 relative">
            {/* Dismiss button */}
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="absolute top-3 right-3 p-1 rounded-full hover:bg-muted transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            )}

            {/* Header */}
            <div className="text-center mb-6">
              <motion.div
                animate={{ rotate: [0, -10, 10, -10, 0] }}
                transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
                className="inline-block mb-4"
              >
                <Gamepad2 className="w-16 h-16 text-primary" />
              </motion.div>
              <h2 className="text-2xl font-black text-foreground mb-2">
                🎮 REMATCH REQUEST!
              </h2>
              <p className="text-muted-foreground">
                <span className="text-primary font-bold">{initiatorName}</span> wants to play again!
              </p>
            </div>

            {/* Timer */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <Clock className="w-5 h-5 text-muted-foreground" />
              <div className="text-lg font-mono">
                <span className={timeRemaining <= 10 ? 'text-destructive animate-pulse' : 'text-foreground'}>
                  {timeRemaining}s
                </span>
                <span className="text-muted-foreground"> remaining</span>
              </div>
            </div>

            {/* Vote Progress */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Player Responses</span>
                </div>
                <span className="text-sm font-bold text-foreground">
                  {uniqueVotes.length}/{totalPlayers}
                </span>
              </div>
              
              {/* Progress bar */}
              <div className="h-3 bg-muted rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-primary"
                  initial={{ width: 0 }}
                  animate={{ width: `${(uniqueVotes.length / totalPlayers) * 100}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>

              {/* Vote breakdown */}
              <div className="flex justify-between mt-2 text-sm">
                <span className="text-chaos-green flex items-center gap-1">
                  <Check className="w-4 h-4" /> {yesVotes} ready
                </span>
                <span className="text-destructive flex items-center gap-1">
                  <X className="w-4 h-4" /> {noVotes} declined
                </span>
              </div>
            </div>

            {/* Voters list */}
            {uniqueVotes.length > 0 && (
              <div className="mb-6 p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground mb-2">Votes:</p>
                <div className="flex flex-wrap gap-2">
                  {uniqueVotes.map(vote => (
                    <span
                      key={vote.id}
                      className={`text-xs px-2 py-1 rounded-full ${
                        vote.vote 
                          ? 'bg-chaos-green/20 text-chaos-green' 
                          : 'bg-destructive/20 text-destructive'
                      }`}
                    >
                      {vote.player_name} {vote.vote ? '✓' : '✗'}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Vote buttons */}
            {!hasVoted ? (
              <div className="flex gap-3">
                <Button
                  onClick={onVoteYes}
                  className="flex-1 bg-chaos-green hover:bg-chaos-green/80 text-black font-bold"
                  size="lg"
                >
                  <Check className="w-5 h-5 mr-2" />
                  I'm In!
                </Button>
                <Button
                  onClick={onVoteNo}
                  variant="outline"
                  className="flex-1 border-destructive text-destructive hover:bg-destructive/10"
                  size="lg"
                >
                  <X className="w-5 h-5 mr-2" />
                  Maybe Later
                </Button>
              </div>
            ) : (
              <div className="text-center p-4 bg-primary/10 rounded-lg">
                <Check className="w-8 h-8 text-primary mx-auto mb-2" />
                <p className="text-foreground font-medium">Vote submitted!</p>
                <p className="text-sm text-muted-foreground">Waiting for other players...</p>
              </div>
            )}
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}